import React, { useState, useEffect, createContext, useContext, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Eagerly load Login (first paint) + Layout (shell)
import Login from "@/pages/Login";
import Layout from "@/components/Layout";

// Lazy-load every other page so each chunk downloads only when visited
const Dashboard         = lazy(() => import("@/pages/Dashboard"));
const Customers         = lazy(() => import("@/pages/Customers"));
const Billing           = lazy(() => import("@/pages/Billing"));
const Products          = lazy(() => import("@/pages/Products"));
const Inventory         = lazy(() => import("@/pages/Inventory"));
const Targets           = lazy(() => import("@/pages/Targets"));
const Leads             = lazy(() => import("@/pages/Leads"));
const DiscountApprovals = lazy(() => import("@/pages/DiscountApprovals"));
const ManagerKitty      = lazy(() => import("@/pages/ManagerKitty"));
const Users             = lazy(() => import("@/pages/Users"));
const Reports           = lazy(() => import("@/pages/Reports"));
const Invoices          = lazy(() => import("@/pages/Invoices"));
const Stores            = lazy(() => import("@/pages/Stores"));
const DamagedGoods      = lazy(() => import("@/pages/DamagedGoods"));
const Purchases         = lazy(() => import("@/pages/Purchases"));
const Quotations        = lazy(() => import("@/pages/Quotations"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [locationBlocked, setLocationBlocked] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  // Geofence check for executives
  const verifyLocation = async (authToken) => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        // No geolocation support — block executive
        resolve({ allowed: false, no_geolocation: true });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const res = await axios.post(`${API}/auth/verify-location`, {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            }, {
              headers: { Authorization: `Bearer ${authToken}` }
            });
            resolve(res.data);
          } catch (error) {
            // API error — block access (fail-safe)
            resolve({ allowed: false, error: "Verification failed" });
          }
        },
        (error) => {
          // Location permission denied — block executive
          resolve({ allowed: false, location_denied: true });
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  };

  // Periodic geofence check every 1 hour for executives
  useEffect(() => {
    if (!user || user.role !== "executive" || !token) return;

    const checkGeofence = async () => {
      const result = await verifyLocation(token);
      if (!result.allowed && !result.no_geofence && !result.location_denied) {
        setLocationBlocked(true);
        toast.error("You are outside the store area. Access restricted.");
        logout();
      }
    };

    // Set up 1-hour interval (skip immediate check — already done at login)
    const intervalId = setInterval(checkGeofence, 60 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [user, token]);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      const { access_token, user: userData } = response.data;

      // Geofence check for executives before completing login
      if (userData.role === "executive") {
        const locationResult = await verifyLocation(access_token);
        if (locationResult.location_denied) {
          const errorMsg = "Location access is required. Please enable GPS and try again.";
          toast.error(errorMsg);
          return { success: false, error: errorMsg };
        } else if (locationResult.no_geolocation) {
          const errorMsg = "Your browser does not support GPS. Use a GPS-enabled device.";
          toast.error(errorMsg);
          return { success: false, error: errorMsg };
        } else if (!locationResult.allowed && !locationResult.no_geofence) {
          const errorMsg = locationResult.distance
            ? `You are ${locationResult.distance}m away from ${locationResult.store_name}. Must be within ${locationResult.radius}m.`
            : "You must be at a store location to access the app.";
          toast.error(errorMsg);
          return { success: false, error: errorMsg };
        } else if (locationResult.store_name) {
          toast.success(`Location verified: ${locationResult.store_name}`);
        }
      }

      localStorage.setItem("token", access_token);
      setToken(access_token);
      setUser(userData);
      setLocationBlocked(false);
      toast.success(`Welcome back, ${userData.full_name}!`);
      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.detail || error.message || "Login failed";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    setLocationBlocked(false);
  };

  // ═══ Auto-logout curfew: executive & warehouse_manager after 21:30 IST ═══
  useEffect(() => {
    if (!user) return;
    const CURFEW_ROLES = ["executive", "warehouse_manager"];
    if (!CURFEW_ROLES.includes(user.role)) return;

    const checkCurfew = () => {
      // Current time in IST (UTC+5:30) regardless of user's local timezone
      const nowUtc = new Date();
      const istMs = nowUtc.getTime() + (5 * 60 + 30) * 60 * 1000;
      const ist = new Date(istMs);
      // Use UTC getters because we already shifted into IST
      const hour = ist.getUTCHours();
      const minute = ist.getUTCMinutes();
      const pastCurfew = hour > 21 || (hour === 21 && minute >= 30);
      // Curfew window: 21:30 IST → 00:00 IST (next day). After midnight, allowed again.
      if (pastCurfew) {
        toast.error("Session ended: access closes at 9:30 PM IST. Please login again after midnight.", { duration: 6000 });
        logout();
      }
    };
    checkCurfew();
    const interval = setInterval(checkCurfew, 30 * 1000); // every 30s
    return () => clearInterval(interval);
  }, [user]);

  const value = {
    user,
    token,
    login,
    logout,
    loading,
    locationBlocked,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to the role's home page
    if (user.role === "warehouse_manager") return <Navigate to="/invoices" replace />;
    if (user.role === "executive") return <Navigate to="/dashboard" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// API Helper
export const apiClient = axios.create({
  baseURL: API,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

const RoleRedirect = () => {
  const { user } = useAuth();
  if (user?.role === "warehouse_manager") return <Navigate to="/invoices" replace />;
  if (user?.role === "executive") return <Navigate to="/dashboard" replace />;
  return <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<ProtectedRoute><RoleRedirect /></ProtectedRoute>} />
          
          <Route element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            <Route path="/dashboard" element={
              <ProtectedRoute allowedRoles={["admin", "manager", "executive"]}>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/customers" element={
              <ProtectedRoute allowedRoles={["admin", "manager"]}>
                <Customers />
              </ProtectedRoute>
            } />
            <Route path="/billing" element={
              <ProtectedRoute allowedRoles={["admin", "manager", "executive"]}>
                <Billing />
              </ProtectedRoute>
            } />
            <Route path="/leads" element={
              <ProtectedRoute allowedRoles={["admin", "manager", "executive"]}>
                <Leads />
              </ProtectedRoute>
            } />
            <Route path="/targets" element={
              <ProtectedRoute allowedRoles={["admin", "manager", "executive"]}>
                <Targets />
              </ProtectedRoute>
            } />
            
            <Route path="/products" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <Products />
              </ProtectedRoute>
            } />
            
            <Route path="/inventory" element={
              <ProtectedRoute allowedRoles={["admin", "manager"]}>
                <Inventory />
              </ProtectedRoute>
            } />
            
            <Route path="/invoices" element={
              <ProtectedRoute allowedRoles={["admin", "manager", "warehouse_manager"]}>
                <Invoices />
              </ProtectedRoute>
            } />
            
            <Route path="/discount-approvals" element={
              <ProtectedRoute allowedRoles={["admin", "manager"]}>
                <DiscountApprovals />
              </ProtectedRoute>
            } />
            
            <Route path="/kitty" element={
              <ProtectedRoute allowedRoles={["admin", "manager"]}>
                <ManagerKitty />
              </ProtectedRoute>
            } />
            
            <Route path="/users" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <Users />
              </ProtectedRoute>
            } />
            
            <Route path="/reports" element={
              <ProtectedRoute allowedRoles={["admin", "manager"]}>
                <Reports />
              </ProtectedRoute>
            } />
            <Route path="/stores" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <Stores />
              </ProtectedRoute>
            } />
            <Route path="/damaged-goods" element={
              <ProtectedRoute allowedRoles={["admin", "manager"]}>
                <DamagedGoods />
              </ProtectedRoute>
            } />
            <Route path="/purchases" element={
              <ProtectedRoute allowedRoles={["admin", "manager"]}>
                <Purchases />
              </ProtectedRoute>
            } />
            <Route path="/quotations" element={
              <ProtectedRoute allowedRoles={["admin", "manager", "executive"]}>
                <Quotations />
              </ProtectedRoute>
            } />
          </Route>
          
          <Route path="*" element={<ProtectedRoute><RoleRedirect /></ProtectedRoute>} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
